// src/pages/PublicPatientForm.js
import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Row, Col, Button, Form as BootstrapForm } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const preventSpaces = (e) => {
  if (e.key === ' ' && (!e.target.value || /^\s*$/.test(e.target.value + e.key))) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};
const preventNonDigits = (e) => {
  if (
    !/[0-9]/.test(e.key) &&
    e.key !== "Backspace" &&
    e.key !== "Delete" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight" &&
    e.key !== "Tab"
  ) {
    e.preventDefault();
  }
};
const preventPasteNonDigits = (e) => {
  const pasteData = e.clipboardData.getData("text");
  if (!/^\d*$/.test(pasteData)) {
    e.preventDefault();
  }
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .test('no-empty-or-spaces', 'Name cannot be empty or spaces only', value => value && value.trim().length > 0 && !/^\s+$/.test(value)),
  email: Yup.string()
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
    .test('no-empty-or-spaces', 'Email cannot be empty or spaces only', value => value && value.trim().length > 0 && !/^\s+$/.test(value)),
  phone: Yup.string()
    .required('Phone number is required')
    .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits'),
  personal_email: Yup.string()
    .optional()
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
    .nullable(),
  extraPhone: Yup.string()
    .optional()
    .matches(/^[0-9]{10}$/, 'Extra phone must be exactly 10 digits')
    .nullable(),
  age: Yup.number().required('Age is required').positive().integer(),
  gender: Yup.string().required('Gender is required'),
});

const PublicPatientForm = () => {
  const [hospitalDetails, setHospitalDetails] = useState({ hospital_id: '', subhospital_id: '' });
  const [receptionistNoPrefix, setReceptionistNoPrefix] = useState('');
  const navigate = useNavigate();
  const generateRandomPassword = () => Math.floor(10000 + Math.random() * 90000).toString();

  useEffect(() => {
    const receptionistData = JSON.parse(localStorage.getItem('authUser'));
    const token = localStorage.getItem('token');
    if (receptionistData && token) {
      const { hospital_id, sub_hospital_id } = receptionistData;
      setHospitalDetails({ hospital_id, subhospital_id: sub_hospital_id });
      axios
        .get(`${BASE_URL}/api/patientAppointment/receptionist-no-prefix`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { hospital_id, subhospital_id: sub_hospital_id },
        })
        .then((response) => setReceptionistNoPrefix(response.data.prefix))
        .catch(() => setReceptionistNoPrefix(''));
    }
  }, []);

  const handleSubmit = (values) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication token is missing. Please log in again.');
      return;
    }
    const payload = {
      ...values,
      password: generateRandomPassword(),
      hospital_id: hospitalDetails.hospital_id,
      subhospital_id: hospitalDetails.subhospital_id,
    };
    axios
      .post(`${BASE_URL}/api/patient/add-patient`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        toast.success('Patient registered! Credentials sent to phone and email.');
        navigate('/login');
      })
      .catch(() => toast.error('Error saving patient data'));
  };

  return (
    <Container className="py-5">
      <Formik
        initialValues={{
          receipt_no: receptionistNoPrefix,
          name: '',
          email: '',
          phone: '',
          personal_email: '',
          extraPhone: '',
          age: '',
          gender: '',
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form style={{ background: 'var(--bg-card)', borderRadius: '10px', boxShadow: 'var(--shadow-light)' }}>
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Receipt No.</BootstrapForm.Label>
                  <Field name="receipt_no" as={BootstrapForm.Control} value={receptionistNoPrefix} disabled />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Name</BootstrapForm.Label>
                  <Field
                    name="name"
                    as={BootstrapForm.Control}
                    placeholder="Enter name"
                    onKeyDown={preventSpaces}
                    onPaste={preventPasteSpaces}
                  />
                  <ErrorMessage name="name" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Email</BootstrapForm.Label>
                  <Field
                    name="email"
                    type="email"
                    as={BootstrapForm.Control}
                    placeholder="Enter email"
                    onKeyDown={preventSpaces}
                    onPaste={preventPasteSpaces}
                  />
                  <ErrorMessage name="email" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Password</BootstrapForm.Label>
                  <Field name="password">
                    {({ field }) => (
                      <input {...field} type="password" className="form-control" value="Auto-generated" disabled />
                    )}
                  </Field>
                </BootstrapForm.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Phone</BootstrapForm.Label>
                  <Field
                    name="phone"
                    as={BootstrapForm.Control}
                    placeholder="Enter phone"
                    maxLength={10}
                    onKeyDown={preventNonDigits}
                    onPaste={preventPasteNonDigits}
                  />
                  <ErrorMessage name="phone" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Personal Email</BootstrapForm.Label>
                  <Field
                    name="personal_email"
                    type="email"
                    as={BootstrapForm.Control}
                    placeholder="Enter personal email"
                    onKeyDown={preventSpaces}
                    onPaste={preventPasteSpaces}
                  />
                  <ErrorMessage name="personal_email" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Extra Phone</BootstrapForm.Label>
                  <Field
                    name="extraPhone"
                    as={BootstrapForm.Control}
                    placeholder="Enter extra phone (optional)"
                    maxLength={10}
                    onKeyDown={preventNonDigits}
                    onPaste={preventPasteNonDigits}
                  />
                  <ErrorMessage name="extraPhone" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Age</BootstrapForm.Label>
                  <Field
                    name="age"
                    type="number"
                    as={BootstrapForm.Control}
                    placeholder="Enter age"
                    min={0}
                  />
                  <ErrorMessage name="age" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <BootstrapForm.Group>
                  <BootstrapForm.Label className="fw-semibold primary-class">Gender</BootstrapForm.Label>
                  <Field as="select" name="gender" className="form-select">
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </Field>
                  <ErrorMessage name="gender" component="div" className="text-danger" />
                </BootstrapForm.Group>
              </Col>
            </Row>
            <Button type="submit" className="global-btn w-100 mt-2 btn-lg">
              Register
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  );
};

export default PublicPatientForm;
